import useSWR from 'swr';
import { fetcher } from '../../../services/fetcher';
import { moduleConfig } from './config';
import {
  buildListQuery,
  ListQueryOptions,
  normalizePaginatedListResponse,
  PaginatedApiResponse,
} from '../../../services/paginatedList';

type CodeName = { id: number; code?: string | null; name?: string | null };
type EquipRef = { id: number; equipmentCode?: string | null; name?: string | null };
type RankRef = { id: number; code?: string | null; name?: string | null; abrv?: string | null };
type CounterRef = {
  id: number;
  name?: string | null;
  unit?: string | null;
  currentValue?: number | string | null;
};

export type PmsPrimarySchedule = {
  id?: number;
  scheduleTypeId?: number | null;
  counterId?: number | null;
  intervalValue?: number | string | null;
  intervalUnitId?: number | null;
  initialDueDate?: string | null;
  lastCompletedDate?: string | null;
  nextDueDate?: string | null;
  lastCompletedCounter?: number | string | null;
  nextDueCounter?: number | string | null;
  warningBeforeValue?: number | string | null;
  warningBeforeUnitId?: number | null;
  gracePeriodValue?: number | string | null;
  gracePeriodUnitId?: number | null;
  allowEarlyCompletion?: boolean | null;
  isActive?: boolean | null;
  scheduleType?: CodeName | null;
  intervalUnit?: CodeName | null;
  warningBeforeUnit?: CodeName | null;
  gracePeriodUnit?: CodeName | null;
  counter?: CounterRef | null;
};

export interface PmsMaintenanceJob {
  id: number;
  equipmentId: number;
  departmentId: number;
  responsibleRankId?: number | null;
  secondaryRankId?: number | null;
  jobCode: string;
  title: string;
  description?: string | null;
  jobTypeId: number;
  maintenanceStrategyId: number;
  criticalityId: number;
  verificationStatusId: number;
  priority?: number | null;
  requiredPersonnel?: number | null;
  permitRequired?: boolean;
  riskAssessmentRequired?: boolean;
  isActive?: boolean;
  equipment?: EquipRef | null;
  department?: CodeName | null;
  responsibleRank?: RankRef | null;
  secondaryRank?: RankRef | null;
  jobType?: CodeName | null;
  maintenanceStrategy?: CodeName | null;
  criticality?: CodeName | null;
  verificationStatus?: CodeName | null;
  primarySchedule?: PmsPrimarySchedule | null;
  dueStatus?: string | null;
  /** Form-only scheduling fields (flattened from primarySchedule). */
  scheduleMode?: string | null;
  counterId?: number | null;
  intervalValue?: number | null;
  intervalUnitId?: number | null;
  initialDueDate?: string | null;
  lastCompletedDate?: string | null;
  nextDueDate?: string | null;
  lastCompletedCounter?: number | null;
  nextDueCounter?: number | null;
  warningBeforeValue?: number | null;
  warningBeforeUnitId?: number | null;
  gracePeriodValue?: number | null;
  gracePeriodUnitId?: number | null;
  createdAt?: string;
  updatedAt?: string;
  [key: string]: unknown;
}

export const usePmsMaintenanceJobs = (
  searchParams?: string,
  page = 1,
  limit = 25,
  options?: ListQueryOptions,
) => {
  const q = buildListQuery(searchParams, page, limit, options);
  const { data, error, isLoading, mutate } = useSWR<
    PaginatedApiResponse<PmsMaintenanceJob> | PmsMaintenanceJob[]
  >(`${moduleConfig.module.api.list}${q}`, fetcher, { revalidateOnFocus: true });
  const normalized = normalizePaginatedListResponse<PmsMaintenanceJob>(data, page, limit);
  return {
    data: normalized.data,
    clients: normalized.data,
    total: normalized.total,
    page: normalized.page,
    limit: normalized.limit,
    isLoading,
    isError: error,
    mutate,
  };
};

export const usePmsMaintenanceJob = (id?: number) => {
  const { data, error, isLoading, mutate } = useSWR<PmsMaintenanceJob>(
    id != null && Number.isFinite(id) && id > 0 ? moduleConfig.module.api.detail(id) : null,
    fetcher,
    { revalidateOnFocus: true },
  );
  return { data, item: data, isLoading, isError: error, mutate };
};
